import React, { useState } from 'react';
import {
  Typography, Card, Table, Button, Modal, Form, Input, Space,
  Popconfirm, message, Empty, Tag, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import { clientsApi, storesApi } from '../api/endpoints';
import type { Client, Store } from '../api/types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ClientsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [storeDetailOpen, setStoreDetailOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [viewingStore, setViewingStore] = useState<Store | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientForm] = Form.useForm();
  const [storeForm] = Form.useForm();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list().then(r => ((r.data as any).data ?? r.data) as Client[]),
  });

  const clientMutation = useMutation({
    mutationFn: (data: { id?: number; name: string }) =>
      data.id ? clientsApi.update(data.id, { name: data.name }) : clientsApi.create({ name: data.name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setClientModalOpen(false);
      clientForm.resetFields();
      message.success(editingClient ? '更新しました' : '作成しました');
      setEditingClient(null);
    },
    onError: () => message.error('保存に失敗しました'),
  });

  const deleteClientMutation = useMutation({
    mutationFn: (id: number) => clientsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      message.success('削除しました');
    },
  });

  const storeMutation = useMutation({
    mutationFn: (data: { id?: number; clientId: number; name: string; analysis_info?: string; persona_info?: string }) =>
      data.id
        ? storesApi.update(data.id, { name: data.name, analysis_info: data.analysis_info, persona_info: data.persona_info } as any)
        : storesApi.create(data.clientId, { name: data.name, analysis_info: data.analysis_info, persona_info: data.persona_info } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setStoreModalOpen(false);
      storeForm.resetFields();
      message.success(editingStore ? '更新しました' : '作成しました');
      setEditingStore(null);
    },
    onError: () => message.error('保存に失敗しました'),
  });

  const deleteStoreMutation = useMutation({
    mutationFn: (id: number) => storesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      message.success('削除しました');
    },
  });

  const openClientModal = (client?: Client) => {
    setEditingClient(client || null);
    clientForm.setFieldsValue(client ? { name: client.name } : { name: '' });
    setClientModalOpen(true);
  };

  const openStoreModal = (clientId: number, store?: Store) => {
    setSelectedClientId(clientId);
    setEditingStore(store || null);
    storeForm.setFieldsValue(store
      ? { name: store.name, analysis_info: store.analysis_info || '', persona_info: store.persona_info || '' }
      : { name: '', analysis_info: '', persona_info: '' }
    );
    setStoreModalOpen(true);
  };

  const hasStoreInfo = (store: Store) =>
    !!(store.analysis_info || store.persona_info);

  const expandedRowRender = (record: Client) => {
    const storeColumns = [
      { title: '店舗名', dataIndex: 'name', key: 'name' },
      {
        title: '情報', key: 'info', width: 80,
        render: (_: any, store: Store) =>
          hasStoreInfo(store) ? (
            <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => { setViewingStore(store); setStoreDetailOpen(true); }}>
              <FileTextOutlined /> 登録済
            </Tag>
          ) : (
            <Text type="secondary">未登録</Text>
          ),
      },
      {
        title: '操作', key: 'action', width: 150,
        render: (_: any, store: Store) => (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openStoreModal(record.id, store)} />
            <Popconfirm title="この店舗を削除しますか？" onConfirm={() => deleteStoreMutation.mutate(store.id)} okText="削除" cancelText="キャンセル" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div style={{ padding: '0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span><ShopOutlined /> 店舗一覧</span>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openStoreModal(record.id)}>
            店舗を追加
          </Button>
        </div>
        {(record.stores?.length || 0) === 0 ? (
          <Empty description="店舗が登録されていません" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" size="small" onClick={() => openStoreModal(record.id)}>最初の店舗を追加</Button>
          </Empty>
        ) : (
          <Table dataSource={record.stores} columns={storeColumns} rowKey="id" pagination={false} size="small" />
        )}
      </div>
    );
  };

  const columns = [
    { title: 'クライアント名', dataIndex: 'name', key: 'name' },
    {
      title: '店舗数', key: 'stores', width: 100,
      render: (_: any, record: Client) => (
        <Tag>{record.stores?.length || 0}店舗</Tag>
      ),
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_: any, record: Client) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openClientModal(record)} />
          <Popconfirm title="このクライアントを削除しますか？（配下の店舗も全て削除されます）" onConfirm={() => deleteClientMutation.mutate(record.id)} okText="削除" cancelText="キャンセル" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>マスター管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openClientModal()}>
          クライアントを追加
        </Button>
      </Space>

      <Card>
        {!isLoading && (clients?.length || 0) === 0 ? (
          <Empty description="クライアントが登録されていません">
            <Button type="primary" onClick={() => openClientModal()}>最初のクライアントを追加</Button>
          </Empty>
        ) : (
          <Table
            dataSource={clients}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            expandable={{ expandedRowRender }}
          />
        )}
      </Card>

      {/* Client Modal */}
      <Modal
        title={editingClient ? 'クライアント編集' : 'クライアント追加'}
        open={clientModalOpen}
        onCancel={() => { setClientModalOpen(false); setEditingClient(null); }}
        onOk={() => clientForm.submit()}
        confirmLoading={clientMutation.isPending}
      >
        <Form form={clientForm} layout="vertical" onFinish={(values) => clientMutation.mutate({ id: editingClient?.id, ...values })}>
          <Form.Item name="name" label="クライアント名" rules={[{ required: true, message: 'クライアント名を入力してください' }]}>
            <Input placeholder="例：株式会社ABC" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Store Modal */}
      <Modal
        title={editingStore ? '店舗編集' : '店舗追加'}
        open={storeModalOpen}
        onCancel={() => { setStoreModalOpen(false); setEditingStore(null); }}
        onOk={() => storeForm.submit()}
        confirmLoading={storeMutation.isPending}
        width={720}
      >
        <Form form={storeForm} layout="vertical" onFinish={(values) => storeMutation.mutate({ id: editingStore?.id, clientId: selectedClientId!, ...values })}>
          <Form.Item name="name" label="店舗名" rules={[{ required: true, message: '店舗名を入力してください' }]}>
            <Input placeholder="例：XXX食堂 水道橋本店" />
          </Form.Item>
          <Form.Item name="analysis_info" label="分析情報（Markdown）" extra="店舗の市場分析、競合分析、ターゲット層などの情報">
            <TextArea rows={8} placeholder="## 市場分析&#10;&#10;- ターゲットエリア: ...&#10;- 競合店舗: ...&#10;- 客層: ..." />
          </Form.Item>
          <Form.Item name="persona_info" label="ペルソナ・差別化情報（Markdown）" extra="想定顧客のペルソナ、店舗の強み・差別化ポイントなど">
            <TextArea rows={8} placeholder="## ペルソナ&#10;&#10;- 年齢層: ...&#10;- 来店動機: ...&#10;&#10;## 差別化ポイント&#10;&#10;- ..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Store Detail Modal (view) */}
      <Modal
        title={`${viewingStore?.name} — 店舗情報`}
        open={storeDetailOpen}
        onCancel={() => { setStoreDetailOpen(false); setViewingStore(null); }}
        footer={null}
        width={720}
      >
        {viewingStore && (
          <Tabs
            items={[
              {
                key: 'analysis',
                label: '分析情報',
                children: viewingStore.analysis_info ? (
                  <div className="markdown-body" style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                    <Markdown>{viewingStore.analysis_info}</Markdown>
                  </div>
                ) : (
                  <Empty description="分析情報が未登録です" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ),
              },
              {
                key: 'persona',
                label: 'ペルソナ・差別化',
                children: viewingStore.persona_info ? (
                  <div className="markdown-body" style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                    <Markdown>{viewingStore.persona_info}</Markdown>
                  </div>
                ) : (
                  <Empty description="ペルソナ・差別化情報が未登録です" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ),
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default ClientsPage;
